import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Card, CardBody, Col, Input, Row, Spinner } from 'reactstrap';
import { Link } from 'react-router-dom';
import MedDataTable from '../common/MedDataTable';
import TablePaginationFooter from '../common/TablePaginationFooter';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders } from '../../helpers/utils';

const BASE_ENDPOINT = `${apiBase}/p-admin/test-assignments.php`;
const OVERVIEW_ENDPOINT = `${BASE_ENDPOINT}?action=overview`;
const DETAIL_ENDPOINT = `${BASE_ENDPOINT}?action=detail`;
const TOGGLE_ENDPOINT = `${BASE_ENDPOINT}?action=toggle`;

const EMPTY_DETAIL = {
  test: null,
  stats: {
    faculty_assignments: 0,
    course_assignments: 0,
    group_assignments: 0,
    student_assignments: 0,
    eligible_students: 0
  },
  filters: {
    faculty: '',
    course: '',
    group: '',
    student_search: ''
  },
  assignments: [],
  faculties: [],
  courses: [],
  groups: [],
  students: [],
  student_pagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0
  }
};

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const stateMeta = state => {
  if (state?.mode === 'direct') {
    return {
      badgeClass: 'psi-test-assign-state psi-test-assign-state--direct',
      label: state.label || 'Bevosita'
    };
  }

  if (state?.mode === 'inherited') {
    return {
      badgeClass: 'psi-test-assign-state psi-test-assign-state--inherited',
      label: state.label || 'Meros'
    };
  }

  return {
    badgeClass: 'psi-test-assign-state psi-test-assign-state--idle',
    label: 'Biriktirilmagan'
  };
};

const scopeTypeLabel = value => {
  if (value === 'faculty') {
    return 'Fakultet';
  }

  if (value === 'course') {
    return 'Kurs';
  }

  if (value === 'group') {
    return 'Guruh';
  }

  if (value === 'student') {
    return 'Talaba';
  }

  return 'Scope';
};

const samePermissions = (left, right) =>
  Boolean(left?.view) === Boolean(right?.view) && Boolean(left?.manage) === Boolean(right?.manage);

const TestAssignmentSettings = () => {
  const [permissions, setPermissions] = useState({ view: false, manage: false });
  const [tests, setTests] = useState([]);
  const [selectedTestKey, setSelectedTestKey] = useState('');
  const [detail, setDetail] = useState(EMPTY_DETAIL);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [banner, setBanner] = useState(null);
  const [testSearch, setTestSearch] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [studentPage, setStudentPage] = useState(1);
  const [quickScopeType, setQuickScopeType] = useState('faculty');
  const [quickScopeValue, setQuickScopeValue] = useState('');

  const applyPermissions = useCallback(nextPermissions => {
    const normalized = nextPermissions || { view: false, manage: false };

    setPermissions(current => (samePermissions(current, normalized) ? current : normalized));
  }, []);

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  const jsonRequestConfig = useMemo(
    () => ({
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    }),
    []
  );

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    setBanner(null);

    try {
      const response = await axios.get(OVERVIEW_ENDPOINT, requestConfig);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Testlar ro`yxati yuklanmadi.');
      }

      const nextTests = response.data?.data?.tests || [];
      applyPermissions(response.data.permissions || { view: false, manage: false });
      setTests(nextTests);
      setPeriodLabel(formatSelectedPeriod(response.data.period));
      setSelectedTestKey(current => {
        if (current && nextTests.some(item => item.key === current)) {
          return current;
        }

        return nextTests[0]?.key || '';
      });
    } catch (error) {
      setBanner({
        color: 'danger',
        text: extractErrorMessage(error, "Test biriktirish oynasini yuklashda xatolik yuz berdi.")
      });
    } finally {
      setLoadingOverview(false);
    }
  }, [applyPermissions, requestConfig]);

  const loadDetail = useCallback(
    async testKey => {
      if (!testKey) {
        setDetail(EMPTY_DETAIL);
        return;
      }

      setLoadingDetail(true);

      try {
        const response = await axios.get(DETAIL_ENDPOINT, {
          ...requestConfig,
          params: {
            kalit: testKey,
            faculty: facultyFilter,
            course: courseFilter,
            group: groupFilter,
            student_search: studentSearch,
            student_page: studentPage,
            student_limit: pageSize
          }
        });

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Test biriktirish ma`lumotlari topilmadi.');
        }

        applyPermissions(response.data.permissions || { view: false, manage: false });
        setDetail(response.data.data || EMPTY_DETAIL);
        setPeriodLabel(formatSelectedPeriod(response.data.period));
      } catch (error) {
        setDetail(EMPTY_DETAIL);
        setBanner({
          color: 'danger',
          text: extractErrorMessage(error, "Tanlangan test bo'yicha ma'lumotni yuklab bo'lmadi.")
        });
      } finally {
        setLoadingDetail(false);
      }
    },
    [applyPermissions, courseFilter, facultyFilter, groupFilter, pageSize, requestConfig, studentPage, studentSearch]
  );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!selectedTestKey) {
      return;
    }

    loadDetail(selectedTestKey);
  }, [loadDetail, selectedTestKey]);

  useEffect(() => {
    setQuickScopeValue('');
  }, [quickScopeType, selectedTestKey]);

  const handlePageSizeChange = value => {
    setPageSize(value);
    setStudentPage(1);
  };

  const handleToggle = useCallback(
    async (scopeType, scopeValue, assign) => {
      if (!selectedTestKey) {
        return;
      }

      const actionKey = `${scopeType}-${scopeValue}-${assign ? 'on' : 'off'}`;
      setBusyAction(actionKey);
      setBanner(null);

      try {
        const response = await axios.post(
          TOGGLE_ENDPOINT,
          {
            kalit: selectedTestKey,
            scope_type: scopeType,
            scope_value: scopeValue,
            assign: assign ? 1 : 0,
            faculty: facultyFilter,
            course: courseFilter,
            group: groupFilter,
            student_search: studentSearch,
            student_page: studentPage,
            student_limit: pageSize
          },
          jsonRequestConfig
        );

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Biriktirishni saqlab bo`lmadi.');
        }

        applyPermissions(response.data.permissions || { view: false, manage: false });
        setDetail(response.data.data || EMPTY_DETAIL);
        setPeriodLabel(formatSelectedPeriod(response.data.period));
        toast.success(response.data.message || (assign ? 'Test biriktirildi.' : 'Test biriktirish bekor qilindi.'));
      } catch (error) {
        const message = extractErrorMessage(error, "Biriktirishni saqlashda xatolik yuz berdi.");
        setBanner({ color: 'danger', text: message });
        toast.error(message);
      } finally {
        setBusyAction('');
      }
    },
    [applyPermissions, courseFilter, facultyFilter, groupFilter, jsonRequestConfig, pageSize, selectedTestKey, studentPage, studentSearch]
  );

  const filteredTests = useMemo(() => {
    const query = testSearch.trim().toLowerCase();

    return tests.filter(test => {
      if (!query) {
        return true;
      }

      return [test.title, test.key].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(query)
      );
    });
  }, [testSearch, tests]);

  const selectedTest = detail.test || tests.find(item => item.key === selectedTestKey) || null;
  const canManage = Boolean(permissions.manage);
  const quickOptions = useMemo(() => {
    if (quickScopeType === 'faculty') {
      return detail.faculties.map(item => ({
        value: String(item.code || item.name),
        label: `${item.name} (${item.student_count} talaba)`,
        state: item.state
      }));
    }

    if (quickScopeType === 'course') {
      return detail.courses.map(item => ({
        value: String(item.value),
        label: `${item.name} (${item.student_count} talaba)`,
        state: item.state
      }));
    }

    if (quickScopeType === 'group') {
      return detail.groups.map(item => ({
        value: String(item.name),
        label: `${item.name} / ${item.course_label || ''} / ${item.faculty_name}`,
        state: item.state
      }));
    }

    return detail.students.map(item => ({
      value: String(item.talaba_id),
      label: `${item.full_name} / ${item.group}`,
      state: item.state
    }));
  }, [detail.courses, detail.faculties, detail.groups, detail.students, quickScopeType]);

  const quickSelectedOption = useMemo(
    () => quickOptions.find(item => item.value === String(quickScopeValue)) || null,
    [quickOptions, quickScopeValue]
  );
  const quickSelectedDirect =
    quickSelectedOption?.state?.mode === 'direct' && quickSelectedOption?.state?.source === quickScopeType;
  const quickActionKey = `${quickScopeType}-${quickScopeValue}-${quickSelectedDirect ? 'off' : 'on'}`;
  const quickActionBusy = busyAction === quickActionKey;

  const groupColumns = useMemo(
    () => [
      {
        dataField: 'name',
        text: 'Guruh',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div>
            <strong>{value}</strong>
            <div className="text-500 fs--1">
              {row.course_label || "Kurs ko'rsatilmagan"} / {row.faculty_name}
            </div>
          </div>
        )
      },
      {
        dataField: 'student_count',
        text: 'Talaba',
        sort: true,
        classes: 'align-middle'
      },
      {
        dataField: 'state',
        text: 'Holat',
        classes: 'align-middle',
        formatter: value => {
          const meta = stateMeta(value);

          return <Badge className={meta.badgeClass}>{meta.label}</Badge>;
        }
      },
      {
        dataField: 'actions',
        text: 'Amal',
        classes: 'align-middle',
        headerStyle: () => ({ minWidth: '11rem' }),
        formatter: (_, row) => {
          const meta = stateMeta(row.state);
          const isDirect = row.state?.mode === 'direct' && row.state?.source === 'group';
          const actionKey = `group-${row.name}-${isDirect ? 'off' : 'on'}`;
          const isBusy = busyAction === actionKey;

          if (!canManage) {
            return <Badge className={meta.badgeClass}>{meta.label}</Badge>;
          }

          return isDirect ? (
            <Button color="light" size="sm" disabled={isBusy} onClick={() => handleToggle('group', row.name, false)}>
              {isBusy ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="unlink" className="mr-2" />}
              Bekor qilish
            </Button>
          ) : (
            <Button color="primary" size="sm" disabled={isBusy} onClick={() => handleToggle('group', row.name, true)}>
              {isBusy ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="link" className="mr-2" />}
              {row.state?.mode === 'inherited' ? 'Bevosita biriktirish' : 'Biriktirish'}
            </Button>
          );
        }
      }
    ],
    [busyAction, canManage, handleToggle]
  );

  const statCards = [
    {
      title: 'Fakultet biriktirish',
      value: detail.stats.faculty_assignments,
      icon: 'building',
      tone: 'primary'
    },
    {
      title: 'Kurs biriktirish',
      value: detail.stats.course_assignments,
      icon: 'layer-group',
      tone: 'secondary'
    },
    {
      title: 'Guruh biriktirish',
      value: detail.stats.group_assignments,
      icon: 'users',
      tone: 'info'
    },
    {
      title: 'Talaba biriktirish',
      value: detail.stats.student_assignments,
      icon: 'user-check',
      tone: 'success'
    },
    {
      title: 'Ruxsatli talabalar',
      value: detail.stats.eligible_students,
      icon: 'shield-alt',
      tone: 'warning'
    }
  ];

  return (
    <div className="psi-test-assign-page">
      <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-3">
        <div className="mb-3 mb-xl-0">
          <Badge color="soft-primary" pill className="mb-2">
            Test sozlamalari
          </Badge>
          <h3 className="mb-1">Test biriktirish oynasi</h3>
          <p className="text-600 mb-0">
            Testlarni fakultet, kurs, guruh va talaba kesimida joriy {periodLabel || 'davr'} uchun biriktiring. Talaba kirishi shu
            oynadagi qoidaga qarab ishlaydi.
          </p>
        </div>

        <div className="d-flex flex-wrap">
          {periodLabel ? (
            <Badge color="soft-info" pill className="mr-2 mb-2">
              {periodLabel}
            </Badge>
          ) : null}
          <Link to="/test-sozlamalari/davr" className="btn btn-light btn-sm mb-2">
            <FontAwesomeIcon icon="calendar-alt" className="mr-2" />
            Davr sozlamasi
          </Link>
        </div>
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      {!permissions.view && !loadingOverview ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="med-testlab-empty-state">
            <FontAwesomeIcon icon="lock" />
            <strong>Ruxsat talab qilinadi</strong>
            <span>Bu bo`lim bilan ishlash uchun test biriktirish bo`yicha ruxsat kerak.</span>
          </CardBody>
        </Card>
      ) : (
        <Row>
          <Col xl="4" className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="psi-test-assign-panel">
                <div className="psi-test-assign-panel__head">
                  <div>
                    <Badge color="soft-info" pill className="mb-2">
                      Testlar ro`yxati
                    </Badge>
                    <h5 className="mb-1">Aktiv testlar</h5>
                    <p className="text-500 mb-0">Biriktirishni boshqarish uchun test tanlang.</p>
                  </div>
                  {loadingOverview ? <Spinner size="sm" color="primary" /> : <Badge color="light">{tests.length} ta</Badge>}
                </div>

                <Input
                  value={testSearch}
                  onChange={event => setTestSearch(event.target.value)}
                  placeholder="Test nomi yoki kalit bo`yicha qidiring"
                  className="psi-test-assign-search mb-3"
                />

                {loadingOverview ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" className="mb-3" />
                    <div className="text-600">Testlar yuklanmoqda...</div>
                  </div>
                ) : filteredTests.length > 0 ? (
                  <div className="psi-test-assign-test-list">
                    {filteredTests.map(test => {
                      const isActive = test.key === selectedTestKey;

                      return (
                        <button
                          key={test.key}
                          type="button"
                          className={classNames('psi-test-assign-test', {
                            'is-active': isActive
                          })}
                          onClick={() => {
                            setSelectedTestKey(test.key);
                            setStudentPage(1);
                          }}
                        >
                          <div className="psi-test-assign-test__top">
                            <strong>{test.title}</strong>
                            <Badge color={isActive ? 'primary' : 'light'}>{test.assignment_count}</Badge>
                          </div>
                          <div className="psi-test-assign-test__meta">
                            <span>{test.group_count} ta guruhda ochilgan</span>
                            <span>{test.question_count} ta savol</span>
                          </div>
                          <div className="psi-test-assign-test__chips">
                            <span>F: {test.faculty_assignment_count}</span>
                            <span>K: {test.course_assignment_count}</span>
                            <span>G: {test.group_assignment_count}</span>
                            <span>T: {test.student_assignment_count}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-500 py-5">Mos test topilmadi.</div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl="8">
            {loadingDetail ? (
              <Card className="border-0 shadow-sm">
                <CardBody className="text-center py-6">
                  <Spinner color="primary" className="mb-3" />
                  <div className="text-600">Tanlangan test bo'yicha biriktirishlar yuklanmoqda...</div>
                </CardBody>
              </Card>
            ) : selectedTest ? (
              <>
                <Card className="border-0 shadow-sm mb-3 psi-test-assign-hero">
                  <CardBody>
                    <div className="psi-test-assign-hero__head">
                      <div>
                        <Badge color="soft-primary" pill className="mb-2">
                          Tanlangan test
                        </Badge>
                        <h4 className="mb-1">{selectedTest.title}</h4>
                        <p className="text-600 mb-0">
                          Kalit: <strong>{selectedTest.key}</strong> . Faol davrda shu testga kirish faqat quyidagi
                          biriktirish qatlamidan o`tadi.
                        </p>
                      </div>
                      <div className="psi-test-assign-hero__chips">
                        <Badge color="light" pill>
                          {selectedTest.group_count || 0} ta baza guruhi
                        </Badge>
                        <Badge color="light" pill>
                          {selectedTest.duration || 0} daqiqa
                        </Badge>
                      </div>
                    </div>

                    <Row className="mt-3">
                      {statCards.map(card => (
                        <Col md="6" xl="3" key={card.title} className="mb-3">
                          <div className={`psi-test-assign-stat psi-test-assign-stat--${card.tone}`}>
                            <span className="psi-test-assign-stat__icon">
                              <FontAwesomeIcon icon={card.icon} />
                            </span>
                            <strong>{card.value}</strong>
                            <span>{card.title}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>

                <Card className="border-0 shadow-sm mb-3">
                  <CardBody className="psi-test-assign-panel">
                    <div className="psi-test-assign-panel__head mb-3">
                      <div>
                        <Badge color="soft-primary" pill className="mb-2">
                          Biriktirish qo`shish
                        </Badge>
                        <h5 className="mb-1">Testni tanlab biriktirish</h5>
                        <p className="text-500 mb-0">
                          Avval test tanlanadi, keyin fakultet, guruh yoki talabani tanlab testga biriktiriladi.
                        </p>
                      </div>
                    </div>

                    <Row>
                      <Col md="4" className="mb-3">
                        <label className="fs--1 font-weight-semi-bold text-700 d-block mb-2">1. Test</label>
                        <Input type="text" value={selectedTest?.title || ''} readOnly />
                      </Col>
                      <Col md="3" className="mb-3">
                        <label className="fs--1 font-weight-semi-bold text-700 d-block mb-2">2. Kesim turi</label>
                        <Input
                          type="select"
                          value={quickScopeType}
                          onChange={event => setQuickScopeType(event.target.value)}
                        >
                          <option value="faculty">Fakultet</option>
                          <option value="course">Kurs</option>
                          <option value="group">Guruh</option>
                          <option value="student">Talaba</option>
                        </Input>
                      </Col>
                      <Col md="5" className="mb-3">
                        <label className="fs--1 font-weight-semi-bold text-700 d-block mb-2">
                          3. {scopeTypeLabel(quickScopeType)} tanlang
                        </label>
                        <Input
                          type="select"
                          value={quickScopeValue}
                          onChange={event => setQuickScopeValue(event.target.value)}
                        >
                          <option value="">
                            {quickScopeType === 'student'
                              ? 'Avval talabani tanlang'
                              : `${scopeTypeLabel(quickScopeType)} tanlang`}
                          </option>
                          {quickOptions.map(option => (
                            <option key={`${quickScopeType}-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                        {quickScopeType === 'student' ? (
                          <small className="text-500 d-block mt-2">
                            Talabani tanlash uchun pastdagi qidiruv va sahifalash ishlatiladi.
                          </small>
                        ) : null}
                      </Col>
                    </Row>

                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center">
                      <div className="mb-3 mb-lg-0">
                        {quickSelectedOption ? (
                          <Badge className={stateMeta(quickSelectedOption.state).badgeClass}>
                            {stateMeta(quickSelectedOption.state).label}
                          </Badge>
                        ) : (
                          <span className="text-500 fs--1">
                            Biriktirish uchun {scopeTypeLabel(quickScopeType).toLowerCase()} tanlang.
                          </span>
                        )}
                      </div>

                      <div className="d-flex flex-wrap">
                        {quickSelectedDirect ? (
                          <Button
                            color="light"
                            disabled={quickActionBusy || !quickScopeValue || !canManage}
                            onClick={() => handleToggle(quickScopeType, quickScopeValue, false)}
                          >
                            {quickActionBusy ? (
                              <Spinner size="sm" className="mr-2" />
                            ) : (
                              <FontAwesomeIcon icon="unlink" className="mr-2" />
                            )}
                            Biriktirishni bekor qilish
                          </Button>
                        ) : (
                          <Button
                            color="primary"
                            disabled={quickActionBusy || !quickScopeValue || !canManage}
                            onClick={() => handleToggle(quickScopeType, quickScopeValue, true)}
                          >
                            {quickActionBusy ? (
                              <Spinner size="sm" className="mr-2" />
                            ) : (
                              <FontAwesomeIcon icon="link" className="mr-2" />
                            )}
                            Testga biriktirish
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Row>
                  <Col lg="12" className="mb-3">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="psi-test-assign-panel">
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                          <div className="mb-3 mb-lg-0">
                            <Badge color="soft-warning" pill className="mb-2">
                              Kurs, guruh va talaba kesimi
                            </Badge>
                            <h5 className="mb-1">Aniqroq biriktirish</h5>
                            <p className="text-500 mb-0">Kerak bo`lsa fakultet ustidan kurs, guruh yoki alohida talaba bo`yicha bevosita yozuv yarating.</p>
                          </div>

                          <div className="psi-test-assign-filters">
                            <Input
                              type="select"
                              value={facultyFilter}
                              onChange={event => {
                                setFacultyFilter(event.target.value);
                                setGroupFilter('');
                                setStudentPage(1);
                              }}
                              className="psi-test-assign-filter"
                            >
                              <option value="">Barcha fakultetlar</option>
                              {detail.faculties.map(faculty => (
                                <option key={faculty.code || faculty.name} value={faculty.code || faculty.name}>
                                  {faculty.name}
                                </option>
                              ))}
                            </Input>
                            <Input
                              type="select"
                              value={courseFilter}
                              onChange={event => {
                                setCourseFilter(event.target.value);
                                setGroupFilter('');
                                setStudentPage(1);
                              }}
                              className="psi-test-assign-filter"
                            >
                              <option value="">Barcha kurslar</option>
                              {detail.courses.map(course => (
                                <option key={course.value} value={course.value}>
                                  {course.name}
                                </option>
                              ))}
                            </Input>
                            <Input
                              type="select"
                              value={groupFilter}
                              onChange={event => {
                                setGroupFilter(event.target.value);
                                setStudentPage(1);
                              }}
                              className="psi-test-assign-filter"
                            >
                              <option value="">Barcha guruhlar</option>
                              {detail.groups.map(group => (
                                <option key={group.name} value={group.name}>
                                  {group.name}
                                </option>
                              ))}
                            </Input>
                            <Input
                              value={studentSearch}
                              onChange={event => {
                                setStudentSearch(event.target.value);
                                setStudentPage(1);
                              }}
                              placeholder="Talaba qidirish"
                              className="psi-test-assign-filter psi-test-assign-filter--search"
                            />
                          </div>
                        </div>

                        <Row>
                          <Col xl="5" className="mb-3 mb-xl-0">
                            <div className="psi-test-assign-subpanel">
                              <div className="psi-test-assign-subpanel__head">
                                <strong>Guruhlar</strong>
                                <Badge color="light">{detail.groups.length} ta</Badge>
                              </div>
                              <MedDataTable
                                data={detail.groups}
                                columns={groupColumns}
                                keyField="name"
                                pageSize={10}
                                paginationResetKey={`${selectedTestKey}-${facultyFilter}-${courseFilter}-${groupFilter}-groups`}
                                wrapperClasses="table-responsive psi-test-assign-table-wrap"
                                tableClasses="table table-hover fs--1 mb-0 med-dashboard-table"
                              />
                            </div>
                          </Col>

                          <Col xl="7">
                            <div className="psi-test-assign-subpanel">
                              <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                                <div>
                                  <strong>Talabalar</strong>
                                  <div className="text-500 fs--1">Biriktirishni talaba darajasida ham boshqaring.</div>
                                </div>
                                <div className="d-flex align-items-center flex-wrap mt-2 mt-sm-0">
                                  <Badge color="light" className="mr-2 mb-2 mb-sm-0">
                                    {detail.student_pagination?.total || 0} ta
                                  </Badge>
                                  <TablePageSizeSelect value={pageSize} onChange={handlePageSizeChange} className="mt-2 mt-sm-0" />
                                </div>
                              </div>

                              {detail.students.length > 0 ? (
                                <>
                                  <div className="table-responsive psi-test-assign-table-wrap">
                                    <table className="table table-hover fs--1 mb-0 med-dashboard-table">
                                      <thead className="bg-200 text-900">
                                        <tr>
                                          <th className="align-middle">T/R</th>
                                          <th className="align-middle">Talaba</th>
                                          <th className="align-middle">Kurs</th>
                                          <th className="align-middle">Pasport</th>
                                          <th className="align-middle">Holat</th>
                                          <th className="align-middle" style={{ minWidth: '11rem' }}>
                                            Amal
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detail.students.map(row => {
                                          const isDirect = row.state?.mode === 'direct' && row.state?.source === 'student';
                                          const actionKey = `student-${row.talaba_id}-${isDirect ? 'off' : 'on'}`;
                                          const isBusy = busyAction === actionKey;
                                          const meta = stateMeta(row.state);

                                          return (
                                            <tr key={row.talaba_id}>
                                              <td className="align-middle font-weight-semi-bold">{row.order}</td>
                                              <td className="align-middle">
                                                <div className="psi-review-student">
                                                  <div className="psi-review-student__avatar">{row.initials}</div>
                                                  <div className="psi-review-student__copy">
                                                    <strong>{row.full_name}</strong>
                                                    <span>
                                                      {row.group} / {row.faculty}
                                                    </span>
                                                  </div>
                                                </div>
                                              </td>
                                              <td className="align-middle">{row.course_label || "Ko'rsatilmagan"}</td>
                                              <td className="align-middle">{row.passport || 'Mavjud emas'}</td>
                                              <td className="align-middle">
                                                <Badge className={meta.badgeClass}>{meta.label}</Badge>
                                              </td>
                                              <td className="align-middle">
                                                {canManage ? (
                                                  isDirect ? (
                                                    <Button color="light" size="sm" disabled={isBusy} onClick={() => handleToggle('student', row.talaba_id, false)}>
                                                      {isBusy ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="unlink" className="mr-2" />}
                                                      Bekor qilish
                                                    </Button>
                                                  ) : (
                                                    <Button color="primary" size="sm" disabled={isBusy} onClick={() => handleToggle('student', row.talaba_id, true)}>
                                                      {isBusy ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="link" className="mr-2" />}
                                                      {row.state?.mode === 'inherited' ? 'Bevosita biriktirish' : 'Biriktirish'}
                                                    </Button>
                                                  )
                                                ) : (
                                                  <Badge className={meta.badgeClass}>{meta.label}</Badge>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>

                                  <TablePaginationFooter
                                    totalItems={detail.student_pagination?.total || 0}
                                    currentPage={detail.student_pagination?.page || 1}
                                    pageSize={detail.student_pagination?.limit || pageSize}
                                    onPageChange={setStudentPage}
                                  />
                                </>
                              ) : (
                                <div className="text-center py-4 text-500">Bu filtr bo`yicha talaba topilmadi.</div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardBody className="text-center py-6">
                  <div className="psi-review-empty-icon mb-3">TB</div>
                  <h5 className="mb-2">Test tanlanmagan</h5>
                  <p className="text-600 mb-0">Chap paneldan test tanlang, so`ng biriktirish oynasi shu yerda ochiladi.</p>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TestAssignmentSettings;
